/* eslint-disable react/jsx-no-bind */
//@flow
import React, { PureComponent } from 'react';
import { themr } from 'react-css-themr';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { translate } from 'react-i18next';
import compose from 'recompose/compose';
import AppPreferencesActions from 'common/redux/actions/appPreferences';
import PjpActions from 'common/redux/actions/pjpMaster';
import { Route } from 'react-router-dom';
import { ButtonTypes, ButtonWithPopover } from 'common/components/core/button';

import {
    clientSelector,
    isAddEntityActive as isAddEntityActiveSelector
} from 'common/redux/selectors/queryParamsSelectors';
import PjpSelectors from 'common/redux/selectors/pjpMaster';
import { teamsSelector } from 'common/redux/selectors/teamSelector';
import { ConnectedTeamSelectorFilter } from 'common/entity/components/TeamSelectorFilter';
import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';

import {
    PermanentJourneyPlan,
    PjpPlanStatus
} from 'common/models/internal/PermanentJourneyPlan';

import { getEntitySettings } from 'common/redux/selectors/clientSelector';
import { getEntityTablePreferences } from 'common/redux/selectors/appPreferences';
import { getSalesCategories } from 'common/redux/selectors/salesCategories';
import SalesCategoriesActions from 'common/redux/actions/salesCategories';

import { verifyPjpLimitApi, pjpPlanFreeze } from 'common/middleware/api';

import { withRouter } from 'common/components/core/router';

import defaultTheme from 'common/entity/BulkEditContainer.scss';
import specificTheme from 'common/entity/pjpMaster/PjpMasterContainer.scss';

import MasterExportContainer from 'common/entity/generic/MasterExportContainer';
import { entityMasterFactory } from 'common/entity/generic/MasterContainer';
import MasterContainerHOC from 'common/entity/generic/MasterContainerHOC';
import CreateDistBeatDialog from 'common/entity/pjpMaster/CreateDistBeatDialog';
import CreateExistingBeatDialog from 'common/entity/pjpMaster/CreateExistingBeatDialog';

import type { EntitySettings } from 'common/models/internal/EntitySettings';
import type { TFunction } from 'react-i18next';
import { Loader } from 'common/components/core/loader';
import { SizeEnum } from 'common/enums/UIEnums';
import {
    StickyPositionEnum,
    CellDataTypes,
    FROZEN_COLUMN_LIMIT
} from 'common/components/core/table';
import { RouterLink } from 'common/components/core/link/RouterLink';
import TeamChip from 'common/components/core/common/TeamChip';
import CreatePjpContainer from 'common/entity/pjpMaster/create/CreatePjpContainer';
import OutletMasterDialog from 'common/entity/pjpMaster/OutletMasterDialog';
import RiderMasterDialog from 'common/entity/pjpMaster/RiderMasterDialog';
import { Map } from 'immutable';
import PjpExportHandler from './PjpExportHandler';
import ToastActions from 'common/redux/actions/toast';
import {
    PopoverPlaceEnum,
    PopoverTriggerEnum
} from 'common/components/core/popover/popOverFactory';
import NotesButtonWithPopover from 'common/components/core/table/NotesList';
import { TeamStatusEnum } from 'common/enums/TeamEnum';
import VisitUtils from 'common/utils/VisitUtils';
import { isGlobalPersonnelSelector } from 'common/redux/selectors/sessionSelector';
import GeneralFilter from 'common/entity/components/GeneralFilter';
import { Icon, Box, Flex, Tooltip } from '@locus-taxy/neo-ui';
import { ConfirmationDialog } from 'common/components/core/dialog/ConfirmationDialog';
import pjpIds from 'common/tests/masterManagement/pjpPlans.json';
import IncrementalPjpPlan from './incremental/IncrementalPjpPlan';
import {
    PermissionMatch,
    AccessDeniedMessage,
    RoleOperationEnum,
    AccessDeniedDialog
} from 'common/modules/rbac';

type PaginationInfoDataType = {
    currentPage: number,
    numberOfPages: number,
    total: number
};

type PjpDataType = {
    pjps: Array<PermanentJourneyPlan>,
    paginationInfo: PaginationInfoDataType
};

type Props = {
    clientId: string,
    theme: Object,
    data: PjpDataType,
    entitySettings: ?EntitySettings,
    tableSettings: ?Array<Object>,
    t: TFunction,
    fetchEntities: ({ clientId: string }, Object) => void,
    updatedPjp: ({ clientId: string, pjpId: string }) => void,
    isGlobalPersonnel: boolean
};

type State = {
    expandedRows: Map<number, boolean>,
    planIdToExport: string,
    isExportEntityActive: boolean,
    planToFreeze: string,
    showPlanFreezeConfirmation: boolean,
    pjpLimitStatus: string
};

const ENTITY_TABLE_ID = 'PjpMaster';
const ENTITY_EXPORT_ID = 'PJP_EXPORT';

export const COLUMNS = [
    {
        id: 'planFreeze',
        Cell: props => {
            const {
                original,
                row,
                tdProps: {
                    rest: { theme, freezePlan, t }
                }
            } = props;
            const pjPlanStatus = original.getEffectiveStatus();
            if (pjPlanStatus === PjpPlanStatus.Error) {
                return null;
            }
            if (pjPlanStatus === PjpPlanStatus.Finalised) {
                return (
                    <Box
                        sx={{ textAlign: 'center', cursor: 'not-allowed' }}
                        width="100%"
                    >
                        <Tooltip tooltip={t('pjpTable.finalPlan')}>
                            <Icon
                                size={Icon.Size.medium}
                                color="red.500"
                                icon="lock"
                                pr="3px"
                            />
                        </Tooltip>
                    </Box>
                );
            }
            return (
                <Box width="100%">
                    <Flex justifyContent="center">
                        <PermissionMatch
                            operations={RoleOperationEnum.EDIT_PJP}
                            render={hasPermission => (
                                <ButtonWithPopover
                                    testId={`${pjpIds.table.planFreeze}-${row._index}`}
                                    buttonType={ButtonTypes.Icon}
                                    theme={theme}
                                    disableArrow
                                    onClick={freezePlan}
                                    place={PopoverPlaceEnum.Right}
                                    triggerPopoverOn={PopoverTriggerEnum.Hover}
                                    popOverContent={
                                        hasPermission ? (
                                            t('pjpTable.shouldFinalisePlan')
                                        ) : (
                                            <AccessDeniedMessage
                                                action={t(
                                                    'fpPlanView.freezePlan'
                                                )}
                                            />
                                        )
                                    }
                                    disabled={!hasPermission}
                                >
                                    <Icon
                                        size={Icon.Size.medium}
                                        color="grey.800"
                                        icon="unlock"
                                    />
                                </ButtonWithPopover>
                            )}
                        />
                    </Flex>
                </Box>
            );
        },
        headerIcon: 'lock',
        showIconOnly: true,
        headerTextId: 'pjpTable.finalPlan',
        sortable: false,
        accessor: 'planStatus',
        filterable: true,
        width: 80,
        FilterComponent: GeneralFilter,
        getFilterOptions: ({ column }) => {
            const { t } = column.getHeaderProps();
            return [
                {
                    value: PjpPlanStatus.Frozen,
                    label: t('pjpTable.finalisedPlan')
                },
                {
                    value: 'non-final',
                    label: t('pjpTable.nonFinalizedPlan')
                }
            ];
        },
        getCustomFilterInfo: ({ filteredColumn }) => {
            const { value } = filteredColumn;
            if (value === PjpPlanStatus.Frozen) {
                return new FilterBuilder()
                    .withName('planStatus')
                    .withOperation(FilterOperation.Equals)
                    .withValues([PjpPlanStatus.Frozen]);
            }
            return new FilterBuilder()
                .withName('planStatus')
                .withOperation(FilterOperation.NotEquals)
                .withValues([PjpPlanStatus.Frozen]);
        },
        frozen: true,
        disablePinControl: true
    },
    {
        headerTextId: 'pjpTable.id',
        id: 'id',
        accessor: 'id',
        excludeFromLoading: true,
        infixSearchable: true,
        frozen: true
    },
    {
        headerTextId: 'pjpTable.pjpName',
        infixSearchable: true,
        id: 'name',
        excludeFromLoading: true,
        minWidth: 150,
        accessor: 'name',
        placeholder: 'Add Plan Name',
        isEditable: true
    },
    {
        headerTextId: 'pjpTable.notes',
        id: 'notes',
        accessor: 'notes',
        excludeFromLoading: true,
        filterable: false,
        sortable: false,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme, partialUpdateEntity, t }
                }
            } = props;
            const { notes } = original;
            return (
                <PermissionMatch
                    operations={RoleOperationEnum.CREATE_PJP}
                    render={hasPermission => {
                        return (
                            <>
                                {hasPermission ? (
                                    <NotesButtonWithPopover
                                        className={classnames(
                                            theme.tableButton,
                                            theme.plain
                                        )}
                                        list={notes}
                                        listTitle={t('pjpTable.notes')}
                                        theme={theme}
                                        preferPlace={PopoverPlaceEnum.Left}
                                        updateNotes={partialUpdateEntity}
                                        original={original}
                                        accessor={'notes'}
                                        defaultAddButton
                                        addNewNote
                                    >
                                        {t('pjpTable.add')}
                                    </NotesButtonWithPopover>
                                ) : (
                                    <ButtonWithPopover
                                        uppercase
                                        className={classnames(
                                            theme.cellText,
                                            theme.button,
                                            theme.download
                                        )}
                                        theme={theme}
                                        disabled
                                        popOverContent={
                                            <AccessDeniedMessage
                                                action={t('translations:add')}
                                            />
                                        }
                                        color={'dark-sky-blue'}
                                        triggerPopoverOn={
                                            PopoverTriggerEnum.Hover
                                        }
                                    >
                                        {t('pjpTable.add')}{' '}
                                    </ButtonWithPopover>
                                )}

                                {notes.size ? (
                                    <>
                                        <span className={theme.divider}>
                                            {' '}
                                            |{' '}
                                        </span>
                                        <NotesButtonWithPopover
                                            className={classnames(
                                                theme.tableButton,
                                                theme.plain
                                            )}
                                            list={notes}
                                            listTitle={t('pjpTable.notes')}
                                            theme={theme}
                                            preferPlace={PopoverPlaceEnum.Left}
                                            updateNotes={partialUpdateEntity}
                                            original={original}
                                            accessor={'notes'}
                                            editDisabled={!hasPermission}
                                            deleteDisabled={!hasPermission}
                                            disableEditIcon={!hasPermission}
                                            saveButtonPopover={
                                                !hasPermission && (
                                                    <AccessDeniedMessage
                                                        action={t(
                                                            'translations:save'
                                                        )}
                                                    />
                                                )
                                            }
                                            addButtonPopover={
                                                !hasPermission && (
                                                    <AccessDeniedMessage
                                                        action={t(
                                                            'translations:add'
                                                        )}
                                                    />
                                                )
                                            }
                                            deleteButtonPopover={
                                                !hasPermission && (
                                                    <AccessDeniedMessage
                                                        action={t(
                                                            'translations:delete'
                                                        )}
                                                    />
                                                )
                                            }
                                        >
                                            {t('pjpTable.view')}
                                        </NotesButtonWithPopover>
                                    </>
                                ) : null}
                            </>
                        );
                    }}
                />
            );
        }
    },
    {
        headerTextId: 'pjpTable.team',
        id: 'team',
        sortable: false,
        FilterComponent: ConnectedTeamSelectorFilter,
        excludeFromLoading: true,
        minWidth: 150,
        getExtraFilterProps: () => ({
            isMulti: false,
            disableBlanksSelectOption: true,
            showSelectFooter: false,
            submitOnOutsideClick: true
        }),
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { values, allowEmptyOrNull }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('teamId.teamId')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull)
                .withCaseSensitive(true);
        },
        Cell: props => {
            const {
                row,
                tdProps: {
                    rest: { team, theme }
                }
            } = props;
            return (
                <TeamChip
                    team={team}
                    invert={false}
                    theme={theme}
                    className={classnames({
                        [theme.inactiveTeam]:
                            team?.status === TeamStatusEnum.Inactive
                    })}
                    testId={`${pjpIds.table.teamName}-${row._index}`}
                />
            );
        }
    },
    {
        headerTextId: 'pjpTable.createdOn',
        id: 'createdOn',
        property: 'createdOn',
        filterable: false,
        excludeFromLoading: true,
        minWidth: 150,
        dataType: CellDataTypes.DateTime,
        accessor: 'createdOn'
    },
    {
        headerTextId: 'pjpTable.isIncrementalPlan',
        id: 'isIncremental',
        accessor: 'isIncremental',
        Cell: props => {
            const {
                original: { isIncremental },
                tdProps: {
                    rest: { t }
                }
            } = props;
            return isIncremental
                ? t('riderPersonaTable.boolean.yes')
                : t('riderPersonaTable.boolean.no');
        },
        sortable: false,
        excludeFromLoading: true,
        infixSearchable: false,
        minWidth: 150
    },
    {
        headerTextId: 'pjpTable.incrementalPlan',
        id: 'createIncrementalPlan',
        accessor: 'incrementalPlan',
        excludeFromLoading: false,
        sortable: false,
        infixSearchable: false,
        frozen: false,
        Cell: props => {
            const {
                row,
                original,
                tdProps: {
                    rest: { theme, t }
                }
            } = props;
            const {
                id: planId,
                teamId: { teamId },
                homebaseId,
                clientId,
                planStatus
            } = original;

            if (
                clientId &&
                (planStatus === PjpPlanStatus.Completed ||
                    planStatus === PjpPlanStatus.Frozen)
            ) {
                // eslint-disable-next-line max-len
                const incrementalPlanRoute = `/client/${clientId}/pjp/incremental/${planId}?teamId=${teamId}&homebaseId=${homebaseId}`;
                return (
                    <PermissionMatch
                        operations={RoleOperationEnum.CREATE_PJP}
                        render={hasPermission => (
                            <Tooltip
                                shouldWrapChildren
                                tooltip={
                                    !hasPermission ? (
                                        <AccessDeniedMessage
                                            action={t(
                                                'pjpTable.incrementalPlan'
                                            )}
                                        />
                                    ) : null
                                }
                            >
                                <RouterLink
                                    testId={`${pjpIds.table.createIncrementalPlan}-${row._index}`}
                                    className={classnames(
                                        theme.cellText,
                                        theme.link,
                                        theme.upperCase,
                                        {
                                            [theme.disabled]: !hasPermission
                                        }
                                    )}
                                    to={incrementalPlanRoute}
                                    disabled={!hasPermission}
                                >
                                    +{t('pjpTable.incrementalPlan')}
                                </RouterLink>
                            </Tooltip>
                        )}
                    />
                );
            }
            return '-';
        },
        minWidth: 150
    },
    {
        headerTextId: 'pjpTable.schedulingPlan',
        id: 'schedulingPlan',
        accessor: 'schedulingPlan',
        excludeFromLoading: false,
        sortable: false,
        infixSearchable: true,
        frozen: false,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const { value } = filteredColumn;
            return new FilterBuilder()
                .withName('plan.planId')
                .withOperation(FilterOperation.ContainsToken)
                .withValue(value);
        },
        Cell: props => {
            const {
                row,
                original,
                tdProps: {
                    rest: { theme, t }
                }
            } = props;
            const { clientId, planStatus } = original;
            if (!clientId) {
                return '-';
            }
            let endPath = ``;
            if (
                planStatus === PjpPlanStatus.Completed ||
                planStatus === PjpPlanStatus.Frozen
            ) {
                endPath = `/client/${clientId}/pjp/${original.id}/fpPlan/planView/allocated`;
            } else {
                return '-';
            }

            return (
                <RouterLink
                    testId={`${pjpIds.table.schedulingPlanLink}-${row._index}`}
                    className={classnames(
                        theme.cellText,
                        theme.link,
                        theme.upperCase
                    )}
                    to={endPath}
                >
                    {t('pjpTable.view')}
                </RouterLink>
            );
        }
    },
    {
        headerTextId: 'pjpTable.planMetrics',
        id: 'metricsReport',
        accessor: 'planSummary.metricsReport',
        filterable: false,
        minWidth: 150,
        Cell: props => {
            const {
                original,
                row,
                original: { id },
                tdProps: {
                    rest: { theme, t, onExport }
                }
            } = props;

            if (original.isErrored()) {
                return null;
            }
            const { clientId, planStatus } = original;
            return (
                <>
                    {(planStatus === PjpPlanStatus.Completed ||
                        planStatus === PjpPlanStatus.Frozen) && (
                        <>
                            <RouterLink
                                testId={`${pjpIds.table.planMetric}-${row._index}`}
                                className={classnames(
                                    theme.cellText,
                                    theme.link,
                                    theme.upperCase
                                )}
                                to={{
                                    pathname: `/client/${clientId}/pjp/${id}/fpPlan/insights`,
                                    state: {
                                        onExport
                                    }
                                }}
                            >
                                {t('pjpTable.view')}
                            </RouterLink>
                            <span className={theme.divider}> | </span>
                        </>
                    )}
                    <PermissionMatch
                        operations={RoleOperationEnum.EXPORT_PJP}
                        render={hasPermission => (
                            <ButtonWithPopover
                                popOverContent={
                                    !hasPermission ? (
                                        <AccessDeniedMessage
                                            action={t('pjpTable.download')}
                                        />
                                    ) : null
                                }
                                triggerPopoverOn={PopoverTriggerEnum.Hover}
                                disabled={!hasPermission}
                                testId={`${pjpIds.table.planMetricDownload}-${row._index}`}
                                ripple={false}
                                buttonType={ButtonTypes.Link}
                                label={t('pjpTable.download')}
                                uppercase
                                semibold
                                color={'dark-sky-blue'}
                                theme={theme}
                                className={classnames(
                                    theme.cellText,
                                    theme.button,
                                    theme.download
                                )}
                                fieldId={id}
                                onClick={onExport}
                            />
                        )}
                    />
                </>
            );
        }
    },
    {
        headerTextId: 'pjpTable.distributionBeats',
        id: 'distributionBeats',
        accessor: 'planSummary.distributionBeats',
        filterable: true,
        sortable: false,
        minWidth: 120,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const { value } = filteredColumn;
            return new FilterBuilder()
                .withName('distributionPlan.planId')
                .withOperation(FilterOperation.ContainsToken)
                .withValue(value);
        },
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme, t }
                }
            } = props;
            const { batchId, planId, clientId } = original.distributionPlan;
            const disabled = !planId || !batchId || !clientId;
            const pjPlanStatus = original.getEffectiveStatus();
            if (pjPlanStatus === 'ERROR') {
                return <div className={theme.cellText}>-</div>;
            }
            if (disabled) {
                return (
                    <PermissionMatch
                        operations={RoleOperationEnum.CREATE_PJP}
                        render={hasPermission => (
                            <Tooltip
                                shouldWrapChildren
                                tooltip={
                                    !hasPermission ? (
                                        <AccessDeniedMessage
                                            action={t('pjpTable.create')}
                                        />
                                    ) : null
                                }
                            >
                                <RouterLink
                                    className={classnames(
                                        theme.cellText,
                                        theme.link,
                                        theme.upperCase,
                                        {
                                            [theme.disabled]: !hasPermission
                                        }
                                    )}
                                    to={`/client/${original.clientId}/pjp/${original.id}/create-dist-beat`}
                                    disabled={!hasPermission}
                                >
                                    {t('pjpTable.create')}
                                </RouterLink>
                            </Tooltip>
                        )}
                    />
                );
            }
            return (
                <>
                    <RouterLink
                        disabled={disabled}
                        className={classnames(
                            theme.cellText,
                            theme.link,
                            theme.upperCase
                        )}
                        to={
                            !planId
                                ? ''
                                : `/client/${clientId}/batch/${batchId}/plan/${planId}`
                        }
                    >
                        {t('pjpTable.view')}
                    </RouterLink>
                    <span className={theme.divider}> | </span>
                    <PermissionMatch
                        operations={RoleOperationEnum.CREATE_PJP}
                        render={hasPermission => (
                            <Tooltip
                                shouldWrapChildren
                                tooltip={
                                    !hasPermission ? (
                                        <AccessDeniedMessage
                                            action={t('pjpTable.reCreate')}
                                        />
                                    ) : null
                                }
                            >
                                <RouterLink
                                    className={classnames(
                                        theme.cellText,
                                        theme.link,
                                        theme.upperCase,
                                        {
                                            [theme.disabled]: !hasPermission
                                        }
                                    )}
                                    to={`/client/${original.clientId}/pjp/${original.id}/create-dist-beat`}
                                    disabled={!hasPermission}
                                >
                                    {t('pjpTable.reCreate')}
                                </RouterLink>
                            </Tooltip>
                        )}
                    />
                </>
            );
        }
    },
    {
        headerTextId: 'pjpTable.existingBeats',
        id: 'existingBeats',
        accessor: 'planSummary.existingBeats',
        filterable: true,
        sortable: false,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const { value } = filteredColumn;
            return new FilterBuilder()
                .withName('existingPlan.planId')
                .withOperation(FilterOperation.ContainsToken)
                .withValue(value);
        },
        Cell: props => {
            const {
                row,
                original,
                tdProps: {
                    rest: { theme, t }
                }
            } = props;
            const { batchId, planId, clientId } = original.existingPlan;
            const pjPlanStatus = original.getEffectiveStatus();
            const disabled = !planId || !batchId || !clientId;
            if (pjPlanStatus === 'ERROR') {
                return <div className={theme.cellText}>-</div>;
            }
            if (disabled) {
                return (
                    <PermissionMatch
                        operations={RoleOperationEnum.CREATE_PJP}
                        render={hasPermission => (
                            <Tooltip
                                shouldWrapChildren
                                tooltip={
                                    !hasPermission ? (
                                        <AccessDeniedMessage
                                            action={t('pjpTable.create')}
                                        />
                                    ) : null
                                }
                            >
                                <RouterLink
                                    className={classnames(
                                        theme.cellText,
                                        theme.link,
                                        theme.upperCase,
                                        {
                                            [theme.disabled]: !hasPermission
                                        }
                                    )}
                                    to={`/client/${original.clientId}/pjp/${original.id}/create-existing-beat`}
                                    disabled={!hasPermission}
                                >
                                    {t('pjpTable.create')}
                                </RouterLink>
                            </Tooltip>
                        )}
                    />
                );
            }
            if (!planId) {
                return '-';
            }
            return (
                <RouterLink
                    testId={`${pjpIds.table.existingPlanView}-${row._index}`}
                    disabled={disabled}
                    className={classnames(
                        theme.cellText,
                        theme.link,
                        theme.upperCase
                    )}
                    to={`/client/${clientId}/batch/${batchId}/plan/${planId}`}
                >
                    {t('pjpTable.view')}
                </RouterLink>
            );
        }
    },
    {
        headerTextId: 'pjpTable.updatedBy',
        id: 'updatedBy',
        sortable: false,
        excludeFromLoading: true,
        minWidth: 150,
        filterable: false,
        fieldId: 'auditMetadata.updatedBy',
        Cell: props => {
            const {
                original: plan,
                tdProps: {
                    rest: { isGlobalPersonnel, theme }
                }
            } = props;
            const updatedBy = VisitUtils.formatIDs(
                plan?.auditMetadata?.updatedBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={updatedBy} className={theme.cellText}>
                    {updatedBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'pjpTable.updatedOn',
        accessor: 'auditMetadata.updatedOn',
        id: 'updatedOn',
        sortable: false,
        filterable: false,
        excludeFromLoading: true,
        minWidth: 150,
        dataType: CellDataTypes.DateTime
    },
    {
        headerTextId: 'pjpTable.createdBy',
        id: 'createdBy',
        sortable: false,
        filterable: false,
        excludeFromLoading: true,
        minWidth: 150,
        fieldId: 'auditMetadata.createdBy',
        Cell: props => {
            const {
                original: plan,
                tdProps: {
                    rest: { isGlobalPersonnel, theme }
                }
            } = props;
            const createdBy = VisitUtils.formatIDs(
                plan?.auditMetadata?.createdBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={createdBy} className={theme.cellText}>
                    {createdBy}
                </span>
            );
        }
    },
    {
        id: 'progress',
        filterable: false,
        sortable: false,
        width: 70,
        headerTextId: 'pjpTable.progress',
        excludeFromLoading: true,
        frozen: true,
        disablePinControl: true,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme }
                }
            } = props;
            const progressPercentage = original.getProgressPercentage();

            return original.isInProgress() ? (
                <div className={theme.progressCell}>
                    <Loader size={SizeEnum.Small} active theme={theme} />
                    &nbsp;
                    {progressPercentage} %
                </div>
            ) : null;
        },
        fixedPosition: StickyPositionEnum.Right
    },
    {
        id: 'status',
        filterable: false,
        sortable: false,
        width: 80,
        headerTextId: 'pjpTable.status',
        excludeFromLoading: true,
        frozen: true,
        disablePinControl: true,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme }
                }
            } = props;
            const pjPlanStatus = original.getEffectiveStatus();

            return <div className={theme.progressCell}>{pjPlanStatus}</div>;
        },
        fixedPosition: StickyPositionEnum.Right
    }
];

const PjpMaster = entityMasterFactory({
    EntityEditViewContainer: CreatePjpContainer,
    EntityExportContainer: MasterExportContainer,
    EntityActions: PjpActions,
    COLUMNS,
    EXPORT_HANDLER: new PjpExportHandler(),
    options: {
        entityTableId: 'PjpMaster',
        operations: {
            add: RoleOperationEnum.CREATE_PJP
        }
    }
});

class PjpMasterContainer extends PureComponent<Props, State> {
    masterContainer: ?PjpMaster;

    state = {
        expandedRows: Map(),
        planIdToExport: '',
        isExportEntityActive: false,
        pjpLimitStatus: this.props.t('defaultPjpLimitStatus'),
        planToFreeze: '',
        showPlanFreezeConfirmation: false
    };

    componentDidMount() {
        const { clientId } = this.props;
        const params = { clientId };
        this.getPjpLimitStatus(params);
    }

    getPjpLimitStatus = (
        params: Object = { clientId: this.props.clientId }
    ) => {
        const { t } = this.props;
        verifyPjpLimitApi
            .fetch(params)
            .then(response => this.handlePjpLimitStatus(response))
            .catch(() =>
                this.setState({
                    pjpLimitStatus: t('genericDataFetchErrorWithRefresh')
                })
            );
    };

    handlePjpLimitStatus = ({
        allowed
    }: {
        allowed: boolean,
        ongoingPlansLimitReached: boolean,
        usedPlansLimitReached: boolean
    }) => {
        const { t } = this.props;
        let status = null;

        if (!allowed) {
            status = t('usedPlansLimitReachedMessage');
        }
        this.setState({ pjpLimitStatus: status });
    };

    onExport = (planId = '') => {
        this.setState({
            planIdToExport: planId,
            isExportEntityActive: !!planId
        });
    };

    onPlanFreezeClick = pjpId => {
        this.setState({
            planToFreeze: pjpId,
            showPlanFreezeConfirmation: true
        });
    };

    onPlanFreezeDismisss = () => {
        this.setState({
            planToFreeze: '',
            showPlanFreezeConfirmation: false
        });
    };

    onEntityExportDismiss = () => {
        this.setState({
            planIdToExport: '',
            isExportEntityActive: false
        });
    };

    getAdditionalExportOptions = () => {
        const planId = this.state.planIdToExport;
        return { planId };
    };

    getTdProps = (state, rowInfo) => {
        if (!rowInfo) {
            return {};
        }
        const {
            t,
            entitySettings,
            theme,
            teams,
            clientId,
            isGlobalPersonnel
        } = this.props;
        const {
            original: {
                id,
                teamId: { teamId },
                planStatus
            }
        } = rowInfo;
        const team = teams.get(teamId);
        const isPlanFrozen = planStatus === PjpPlanStatus.Frozen ? true : false;
        return {
            t,
            clientId,
            entitySettings,
            onExport: this.onExport,
            freezePlan: () => {
                if (isPlanFrozen) {
                    return;
                }
                this.onPlanFreezeClick(id);
            },
            className: isPlanFrozen ? 'highlightCell' : null,
            theme,
            team,
            isGlobalPersonnel
        };
    };

    getMasterContainerRef = ref => {
        this.masterContainer = ref;
    };

    freezePlan = () => {
        const { clientId } = this.props;
        const { planToFreeze } = this.state;
        const params = { clientId, pjpId: planToFreeze };
        pjpPlanFreeze.post(params).then(() => {
            this.masterContainer?.reloadData();
            this.setState({
                planToFreeze: '',
                showPlanFreezeConfirmation: false
            });
        });
    };

    onCloseDialog = () => {
        const { match, history } = this.props;
        history.push(match.url);
    };

    onCloseDialogWithReload = reload => {
        if (reload) {
            this.masterContainer.reloadData();
        }
        this.onCloseDialog();
    };

    renderOutletsDialog = routerProps => {
        const { match } = routerProps;
        const { pjpId } = match.params;
        return (
            <OutletMasterDialog
                {...routerProps}
                fetchOptions={{ pjpId }}
                entityNameId="masterOutlet"
                onDismiss={this.onCloseDialog}
            />
        );
    };

    renderRidersDialog = routerProps => {
        const { match } = routerProps;
        const { pjpId } = match.params;
        return (
            <RiderMasterDialog
                {...routerProps}
                fetchOptions={{ pjpId }}
                entityNameId="masterRider"
                onDismiss={this.onCloseDialog}
            />
        );
    };

    renderCreateDistBeatWithRoutePermissions = reloadData => {
        return routerProps => {
            const { match } = routerProps;
            const { pjpId, clientId } = match.params;
            return (
                <PermissionMatch
                    operations={RoleOperationEnum.CREATE_PJP}
                    render={hasPermission => {
                        if (hasPermission) {
                            return (
                                <CreateDistBeatDialog
                                    {...routerProps}
                                    fetchOptions={{ pjpId, clientId }}
                                    onDismiss={this.onCloseDialog}
                                    reloadData={reloadData}
                                />
                            );
                        }
                        return (
                            <AccessDeniedDialog
                                action={this.props.t(
                                    'pjpTable.distributionBeat'
                                )}
                                onDismiss={this.onCloseDialog}
                            />
                        );
                    }}
                />
            );
        };
    };
    renderCreateExistingBeatWithRoutePermissions = reloadData => {
        return routerProps => {
            const { match } = routerProps;
            const { pjpId, clientId } = match.params;
            return (
                <PermissionMatch
                    operations={RoleOperationEnum.CREATE_PJP}
                    render={hasPermission => {
                        if (hasPermission) {
                            return (
                                <CreateExistingBeatDialog
                                    {...routerProps}
                                    fetchOptions={{ pjpId, clientId }}
                                    onDismiss={this.onCloseDialog}
                                    reloadData={reloadData}
                                />
                            );
                        }
                        return (
                            <AccessDeniedDialog
                                action={this.props.t('pjpTable.existingBeats')}
                                onDismiss={this.onCloseDialog}
                            />
                        );
                    }}
                />
            );
        };
    };

    handleRowExpansion = (newExpanded, [index]) => {
        const { expandedRows } = this.state;
        const {
            data: { entities }
        } = this.props;
        const entity = entities[index];
        if (entity.isInProgress()) {
            return;
        }
        const isRowExpanded = newExpanded[index] ? true : false;
        this.setState({
            expandedRows: expandedRows.set(index, isRowExpanded)
        });
    };

    resetExpandedRows = () => {
        const { expandedRows } = this.state;
        this.setState({ expandedRows: expandedRows.clear() });
    };

    isPjpInProgress = (pjp: PermanentJourneyPlan) => {
        return pjp.isInProgress();
    };

    renderIncrementalPlan = routerProps => {
        return (
            <IncrementalPjpPlan
                {...routerProps}
                onDismiss={this.onCloseDialogWithReload}
            />
        );
    };

    renderChildren = () => {
        const { match, theme, t } = this.props;
        const { showPlanFreezeConfirmation } = this.state;
        const info = {
            text: t('pjpTable.planFreezeInfo'),
            icon: 'information',
            color: 'red.500'
        };
        return ({ reloadData }) => {
            return (
                <>
                    <Route
                        path={`${match.path}/incremental/:pjpId`}
                        render={this.renderIncrementalPlan}
                    />
                    <Route
                        path={`${match.path}/:pjpId/outlets`}
                        render={this.renderOutletsDialog}
                    />
                    <Route
                        path={`${match.path}/:pjpId/riders`}
                        render={this.renderRidersDialog}
                    />
                    <Route
                        path={`${match.path}/:pjpId/create-dist-beat`}
                        render={this.renderCreateDistBeatWithRoutePermissions(
                            reloadData
                        )}
                    />
                    <Route
                        path={`${match.path}/:pjpId/create-existing-beat`}
                        render={this.renderCreateExistingBeatWithRoutePermissions(
                            reloadData
                        )}
                    />
                    {showPlanFreezeConfirmation ? (
                        <ConfirmationDialog
                            info={info}
                            theme={theme}
                            acceptActionLabel={t(
                                'pjpTable.planFreezeConfirmAction'
                            )}
                            onAccept={this.freezePlan}
                            onCancel={this.onPlanFreezeDismisss}
                            onDismiss={this.onPlanFreezeDismisss}
                            active
                            innerText={t('pjpTable.planFreezeConfirmation')}
                            innerSubText={t(
                                'pjpTable.planFreezeConfirmationSubText'
                            )}
                            title={t('pjpTable.shouldFinalisePlan')}
                        />
                    ) : null}
                </>
            );
        };
    };

    render() {
        const { t, clientId } = this.props;
        const {
            expandedRows,
            isExportEntityActive,
            pjpLimitStatus
        } = this.state;
        return (
            <PjpMaster
                {...this.props}
                t={t}
                refProvider={this.getMasterContainerRef}
                fetchOptions={{ clientId }}
                emptyStateImageSrc="/img/location-master-empty.png"
                title={t('entities:masterPjp_plural')}
                entityNameId={'masterPjpShort'}
                disableAddWithText={pjpLimitStatus}
                entityId={'pjpId'}
                entitySettingsName={'pjpSettings'}
                getIsRowLoading={this.isPjpInProgress}
                getTdProps={this.getTdProps}
                hideExportInMenu
                disableCheckbox
                collapseOnDataChange={false}
                expanded={expandedRows.toJS()}
                onExpandedChange={this.handleRowExpansion}
                onSortedChange={this.resetExpandedRows}
                onFilteredChange={this.resetExpandedRows}
                onPageChange={this.resetExpandedRows}
                onPageSizeChange={this.resetExpandedRows}
                entityExportId={ENTITY_EXPORT_ID}
                isExportEntityActive={isExportEntityActive}
                onEntityExportDismiss={this.onEntityExportDismiss}
                getAdditionalExportOptions={this.getAdditionalExportOptions}
                hideExportOptions
                hideSubtitle
                hasFreezableColumns
                frozenColumnLimit={FROZEN_COLUMN_LIMIT}
                pinTooltip={t('pinTooltip')}
                unpinTooltip={t('unpinTooltip')}
                updatePjpLimitStatus={this.getPjpLimitStatus}
                defaultSorted={[
                    {
                        id: 'createdOn',
                        desc: true
                    }
                ]}
            >
                {this.renderChildren()}
            </PjpMaster>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const clientId = clientSelector(state, ownProps);
    const isLoading = PjpSelectors.isEntitiesLoading(state, ownProps);
    const data = PjpSelectors.getEntities(state, ownProps);
    const teams = teamsSelector(state, ownProps);
    const isAddEntityActive = isAddEntityActiveSelector(state, ownProps);
    const categories = getSalesCategories(state, ownProps);
    const entitySettings = getEntitySettings(state, ownProps);
    const tableSettings = getEntityTablePreferences(state, ENTITY_TABLE_ID);
    const isGlobalPersonnel = isGlobalPersonnelSelector(state, ownProps);

    return {
        data,
        isLoading,
        clientId,
        teams,
        categories,
        entitySettings,
        tableSettings,
        isAddEntityActive,
        isGlobalPersonnel
    };
};

const withRedux = connect(mapStateToProps, {
    fetchEntities: PjpActions.fetchActiveEntities,
    fetchSalesCategories: SalesCategoriesActions.fetchSalesCategories,
    updateEntity: PjpActions.patchEntity,
    clearData: PjpActions.clearData,
    saveTableSettings: AppPreferencesActions.saveEntityTablePreferences,
    showToastAction: ToastActions.showToast,
    updateToastAction: ToastActions.updateToast,
    hideToastAction: ToastActions.hideToast,
    showSuccessToast: ToastActions.showSuccessToast,
    showErrorToast: ToastActions.showErrorToast
});

const enhance = compose(
    themr(null, defaultTheme),
    themr(null, specificTheme),
    withRouter,
    withRedux,
    translate('entityManagement'),
    MasterContainerHOC
);

const ThemedConnectedPjpMasterContainer = enhance(PjpMasterContainer);

export default ThemedConnectedPjpMasterContainer;
